<template>
  <div class="container-fuid">
    <h2 class="title">Statistics</h2>
    <div class="container">
      <b-card style="text-align: left;" bg-variant="light">
        <h4>Disclaimer! This page pushes the limits of what is possible in a browser. Old hardware or a browser with the typical 101 tabs open will probably crash.</h4>
        <p>Hint, just close the tabs. If you haven't used it in the last 12 hours you don't need it open. And while you are at it, close all those apps that you opened three days ago. Your computer will be a LOT faster. Trust me!!</p>
        <p style="font-size: .65em;">All right I'll get off my soapbox...</p>
        <b-form @submit.prevent="searchRecords">
          <b-form-group
            label="Search Records:"
            description="You can search by business name, location name, plot name or barcode."
          >
            <b-form-input :required="!filterVisible" v-model="string" placeholder="search records"></b-form-input>
          </b-form-group>
          <div style="width: 100%; padding-bottom: 2em; margin-top: -2em;">
           <b-link class="advanced" @click="filterVisible = !filterVisible" >Advanced Search</b-link>
          </div>

          <b-collapse v-model="filterVisible" class="mb-1">
            <b-card>
              <b-row>
                <b-col class="mb-2">
                 
                  <b-input-group>
                    <template v-slot:prepend>
                      <b-form-select v-model="dateSearchType" variant="outline-secondary" >
                        <option>Date Within</option>
                        <option>Date Between</option>
                      </b-form-select> 
                    </template>
                  
                    <b-form-select v-show="dateSearchType === 'Date Within'" v-model="dateWithin" variant="outline-secondary" >
                      <option>1 Day</option>
                      <option>3 Days</option>
                      <option>1 Week</option>
                      <option>2 Weeks</option>
                      <option>1 Month</option>
                      <option>2 Months</option>
                      <option>3 Months</option>
                      <option>4 Months</option>
                      <option>5 Months</option>
                      <option>6 Months</option>
                      <option>8 Months</option>
                      <option>10 Months</option>
                      <option>12 Months</option>
                    </b-form-select> 
                    
                    <b-form-input v-show="dateSearchType !== 'Date Within'" v-model="dateFrom" type="date"></b-form-input>
                    <b-form-input v-model="dateTo" type="date"></b-form-input>
                  </b-input-group>
                  
                </b-col>
                <b-col class="mb-2">
                  <b-input-group prepend="Business Name" >
                    <b-form-input v-model="businessName" ></b-form-input>
                  </b-input-group>
                </b-col>
              </b-row>

              <b-row>
                <b-col class="mb-2">
                  <b-input-group prepend="Location Name" >
                    <b-form-input v-model="locationName" ></b-form-input>
                  </b-input-group>
                </b-col>
                <b-col class="mb-2">
                  <b-input-group prepend="Plot Name" >
                    <b-form-input v-model="plotName" ></b-form-input>
                  </b-input-group>
                </b-col>
              </b-row>

              <b-row>
                <b-col class="mb-2">
                  <b-input-group prepend="Crop Name" >
                    <b-form-input v-model="cropName" ></b-form-input>
                  </b-input-group>
                </b-col>
                <b-col class="mb-2">
                  <b-input-group prepend="Variety Name" >
                    <b-form-input v-model="varietyName" ></b-form-input>
                  </b-input-group>
                </b-col>
              </b-row>
            </b-card>
          </b-collapse>

          <b-button size="sm" variant="purple" type="submit">Search</b-button>

          <div style="display: inline-block; margin-top: 1em;">
            <b-button class="mr-1" @click="pearsonCorr">Pearson</b-button>
            <b-button class="mr-1" @click="createExcelExport">Export Data</b-button>
          </div>
        </b-form>
        <b-form >
          <b-collapse v-model="showControls" class="mt-2">
            <div class="row">
              <div class="col">
                <b-form-group label="Trim Bottom:" description="Enter a number and wait, trimming the data takes a long time and changing the number too often can crash your browser.">
                  <b-input type="number" placeholder="percent to trim" v-model="controls.trimBottom"></b-input>
                </b-form-group>
              </div>
              <div class="col">
                <b-form-group label="Trim Top:">
                  <b-input type="number" placeholder="percent to trim" v-model="controls.trimTop"></b-input>
                </b-form-group>
              </div>
            </div>
          </b-collapse>
        </b-form>
        <b-alert  :show="alert.show" dismissible @dismissed="alert.show = false" :variant="alert.variant">{{alert.message}}</b-alert>
        <p v-show="nTests > 0">{{`Number of sets: ${nTests}`}}</p>
      </b-card>
    </div>

    <div v-show="showPearson" class="full-page container-fluid">
      <div style="margin-bottom: .5em;">
        <div class="stat">{{`Number of Sets: ${nTests}`}}</div>
        <b-button class="stat" @click="saveExcel">Save As Excel</b-button>
        <b-button class="stat" @click="showPearson = false">Close</b-button>
      </div>
      <b-table-simple small striped>
        <b-thead head-variant="dark">
          <b-tr> 
            <b-th>Element</b-th>
            <b-th v-for="head of currentHeaders" :key="head" >{{head}}</b-th>
          </b-tr>
        </b-thead>
        <b-tbody>
          <b-tr v-for="row of corr" :key="row[0]" >
            <b-td class="cell" :style="selectColor(val)" v-for="val of row" :key="val">{{isNaN(parseFloat(val)) ? val : parseFloat(val).toFixed(3)}}</b-td>
          </b-tr>
        </b-tbody>
      </b-table-simple>
    </div>

    <div v-show="showHistogram" class="container-fluid">
      <div class="chart-cont" v-for="(line) of generateHistogram()" :key="line.element">
        <h4>{{line.element}}</h4>
        <div class="stats-line">
          <div class="stat">{{`young min: ${line.young.min}`}}</div>
          <div class="stat">{{`young max: ${line.young.max}`}}</div>
          <div class="stat">{{`young median: ${line.young.median}`}}</div>
        </div>
        <div class="stats-line">
          <div class="stat">{{`young avg: ${line.young.mean}`}}</div>
          <div class="stat">{{`young stdDev: ${line.young.deviation}`}}</div>
        </div>

        <div class="stats-line">
          <div class="stat">{{`old min: ${line.old.min}`}}</div>
          <div class="stat">{{`old max: ${line.old.max}`}}</div>
          <div class="stat">{{`old median: ${line.old.median}`}}</div>
        </div>
        <div class="stats-line">
          <div class="stat">{{`old avg: ${line.old.mean}`}}</div>
          <div class="stat">{{`old stdDev: ${line.old.deviation}`}}</div>
        </div>

        <svg :width="chart.width" :height="chart.height" >

          <rect v-for="(d, i) of line.old.data" :key="`${i}-${line.element}-O`" :x="d.x" :y="d.y" :height="d.height" :width="chart.width / line.old.data.length" fill="green" >
            <title>{{`${d.x0} - ${d.x1}  n${d.length}`}}</title>
          </rect>

          <rect v-for="(d, i) of line.young.data" :key="`${i}-${line.element}`" :x="d.x" :y="d.y" :height="d.height" :width="d.width" fill="lightgreen" fill-opacity="0.7" >
            <title>{{`${d.x0} - ${d.x1}  n${d.length}`}}</title>
          </rect>

          <!-- young std dev -->
          <line :x1="line.young.stdLines[0].x" :x2="line.young.stdLines[0].x" y1="100" :y2="150" style="stroke:black;stroke-width: 1.5" stroke-dasharray="6,3" />
          <line :x1="line.young.stdLines[1].x" :x2="line.young.stdLines[1].x" y1="100" :y2="150" style="stroke:black;stroke-width: 1.5" stroke-dasharray="6,3"/>
          <line :x1="line.young.stdLines[0].x" :x2="line.young.stdLines[1].x" y1="125" :y2="125" style="stroke:black;stroke-width: 1.5" stroke-dasharray="6,3"/>
          <text :x="line.young.stdLines[0].x + (line.young.stdLines[1].x - line.young.stdLines[0].x) / 2" text-anchor="middle" y="122">young</text>
          <!-- old std dev -->
          <line :x1="line.old.stdLines[0].x" :x2="line.old.stdLines[0].x" y1="50" :y2="100" style="stroke:black;stroke-width: 1.5" />
          <line :x1="line.old.stdLines[1].x" :x2="line.old.stdLines[1].x" y1="50" :y2="100" style="stroke:black;stroke-width: 1.5" />
          <line :x1="line.old.stdLines[0].x" :x2="line.old.stdLines[1].x" y1="75" :y2="75" style="stroke:black;stroke-width: 1.5" />
          <text :x="line.old.stdLines[0].x + (line.old.stdLines[1].x - line.old.stdLines[0].x) / 2" text-anchor="middle" y="72">old</text>

        </svg>
      </div>
    </div>

  </div>
</template>

<script>
import { statistics } from "../modules/api";
import { saveJsonToExcel } from "../modules/excel";
import Statistics from "statistics.js";
import { scaleLinear, histogram, median, quantile, mean, deviation } from "d3";
import { mapGetters } from "vuex";

export default {
  name: "statistics",
  data() {
    return {
      string: "",
      filterVisible: true,
      dateWithin: "1 Week",
      dateSearchType: "Date Within",
      dateTo: "",
      dateFrom: "",
      businessName: "",
      locationName: "",
      plotName: "",
      cropName: "",
      varietyName: "",
      type: "aggregate",
      corr: [],
      currentHeaders: ["sugarY", "phY", "ecY", "potassiumY", "calciumY", "magnesiumY", "sodiumY", "ammoniumY", "no3_nitrateY", "nitrogen_in_nitrateY", "nitrogenY", "chlorideY", "sulfurY", "phosphorousY", "silicaY", "ironY", "manganeseY", "zincY", "boronY", "copperY", "molybdenumY", "aluminiumY", "cobaltY", "sugarO", "phO", "ecO", "potassiumO", "calciumO", "magnesiumO", "sodiumO", "ammoniumO", "no3_nitrateO", "nitrogen_in_nitrateO", "nitrogenO", "chlorideO", "sulfurO", "phosphorousO", "silicaO", "ironO", "manganeseO", "zincO", "boronO", "copperO", "molybdenumO", "aluminiumO", "cobaltO"],
      nTests: 0,
      orders: [],
      alert: {
        variant: "warning",
        message: "",
        show: false
      },
      chart: {
        width: 800,
        height: 250
      },
      showHistogram: true,
      showPearson: false,
      showControls: true,
      controls: {
        trimBottom: "",
        trimTop: ""
      }
    };
  },
  methods: {
    async searchRecords() {
      let params = {};

      if (this.dateTo) {
        params.dateTo = this.dateTo;
        params.dateSearchType = this.dateSearchType;
        if (this.dateSearchType === "Date Within") {
          params.dateWithin = this.dateWithin;
        } else {
          params.dateFrom = this.dateFrom;
        }
      }

      let keys = ["businessName", "locationName", "plotName", "cropName", "varietyName"];
      for (let key of keys) {
        if (this[key]) {
          params[key] = this[key];
        }
      }

      if (!this.string && Object.keys(params).length <= 0) {
        return;
      }
      params.limit = 50000;
      params.string = this.string || null;

      this.alert.message = "Sit back and sip your coffee, this can take up to a minute.";
      this.alert.show = true;

      let setup = await statistics.run(params);

      if (setup.data.success) {
        this.orders = [];
        this.pingServer(setup);

      } else {
        this.alert.variant = "warning";
        this.alert.message = setup.data.message;
        this.alert.show = true;
      }
    },
    pingServer(setup) {
      setTimeout(async () => {

        let res = await statistics.run({reqId: setup.data.requestId});
        if (res.data.done) {
          
          this.alert.show = false;
          // this.corr = res.data.corr;
          // this.currentHeaders = res.data.headers;
          // this.nTests = res.data.nTests;
          this.orders = res.data.data.orders;
        } else {
          this.pingServer(setup);
        }
      }, 1000);
    },
    selectColor(val) {
      let style = {backgroundColor: null};
      if (val < 0) { 
        val = Math.abs(val); 
      } 
      if (val > .8) {
        style.backgroundColor = "green";
      } else if (val > .7) {
        style.backgroundColor = "lightgreen";
      } else if (val > .6) {
        style.backgroundColor = "gold";
      } else if (val > .5) {
        style.backgroundColor = "yellow";
      }

      if (val >= 1) {
        style.color = "white";
        style.backgroundColor = "transparent";
      }

      return style;
    },
    saveExcel() {
      saveJsonToExcel([["Element", ...this.currentHeaders], ...this.corr], "correlation.xlsx", {skipHeader: true});
    },
    createStat() {
      let youngValues = this.orders.filter(x => x.plantPart.includes("young")).map(x => x.values);
      let oldValues = this.orders.filter(x => !x.plantPart.includes("young")).map(x => x.values);

      this.nTests = Math.min(youngValues.length, oldValues.length);

      let records = [];
      for (let i = 0; i < this.nTests; i++)
      {
        let row = {};
        for (let val of youngValues[i])
        {
          row[`${val.name.toLowerCase()}Y`] = parseFloat(val.value) || 0;
        }
        for (let val of oldValues[i])
        {
          row[`${val.name.toLowerCase()}O`] = parseFloat(val.value) || 0;
        }
        records.push(row);
      }

      const bodyVars = {};
      for (let val of this.currentHeaders)
      {
        bodyVars[val] = "metric";
      }


      return new Statistics(records, bodyVars);
    },
    // createStat() {
    //   // let youngValues = this.orders.filter(x => x.plantPart.includes("young")).map(x => x.values);
    //   // let oldValues = this.orders.filter(x => !x.plantPart.includes("young")).map(x => x.values);
    //   let { young, old, nTests } = this.cleanedValues;
    //   this.nTests = nTests;

    //   let records = [];
    //   for (let i = 0; i < this.nTests; i++)  {
    //     let row = {};
    //     for (let name of this.elements) {
          
    //       row[`${name.key}Y`] = young[name.key][i] || 0;
    //       row[`${name.key}O`] = old[name.key][i] || 0;
  
    //     }
    //     records.push(row);
    //   }

    //   const bodyVars = {};
    //   for (let val of this.currentHeaders)
    //   {
    //     bodyVars[val] = "metric";
    //   }


    //   return new Statistics(records, bodyVars);
    // },
    pearsonCorr() {
      const stats = this.createStat();
      let corr = [];
      for (let val1 in this.currentHeaders)
      {
        let arr = [this.currentHeaders[val1]];
        for (let val2 in this.currentHeaders)
        {
          let r = stats.correlationCoefficient(this.currentHeaders[val1], this.currentHeaders[val2]);
          arr.push(r ? r.correlationCoefficient : "x");
        }
        corr.push(arr);
      }
      this.corr = corr;
      this.showPearson = true;
    },
    generateHistogram() {
      const data = [];
      for (let val of this.elements) { 
        const element = val.key;    
        let { young, old, nTests } = this.cleanedValues;
        young = young[element];
        old = old[element];
        this.nTests = nTests;

        if (!young || young.length < 1 || !old || old.length < 1) {
          continue;
        }
        
        let allmin = Math.min(...young, ...old);
        let allmax = Math.max(...young, ...old);
        let { x } = this.scale(allmin, allmax);

        let d = this.histogramFunction(young, x);
        let dOld = this.histogramFunction(old, x);
        let maxLength = Math.max(...d.map(x => x.length), ...dOld.map(x => x.length));
        let dev = deviation(young).toFixed(2);
        let avg = mean(young).toFixed(3);
        let max = Math.max(...young);
        let min = Math.min(...young);

        let below = avg - dev;
        let above = parseFloat(avg) + parseFloat(dev);

        if (below < min) {
          below = min;
        }

        let ratio = this.chart.height / maxLength;
        let width = this.chart.width / d.length;
          
        let stdLines = d.map((x, i) => ({x0: x.x0, x1: x.x1, i})).filter(x => {
          if ( below >= x.x0 && below <= x.x1 ) {
            return true;
          } else if (above >= x.x0 && above <= x.x1) {
            return true;
          } else {
            return false;
          }
        }).map(x => ({x: width * x.i}));

        let yData = {
          data: d.map((x, i) => ({
            length: x.length, 
            x0: x.x0, 
            x1: x.x1,
            y: this.chart.height - ratio * x.length,
            x: width * i,
            width,
            height: ratio * x.length
          })),
          maxLength,
          max,
          min,
          median: median(young).toFixed(3),
          mean: avg,
          // percentiles: this.percentiles(young),
          deviation: dev,
          stdLines 
        };

        dev = deviation(old).toFixed(2);
        avg = mean(old).toFixed(3);
        max = Math.max(...old);
        min = Math.min(...old);

        below = avg - dev;
        above = parseFloat(avg) + parseFloat(dev);

        if (below < min) {
          below = min;
        }

        stdLines = d.map((x, i) => ({x0: x.x0, x1: x.x1, i})).filter(x => {
          if ( below >= x.x0 && below <= x.x1 ) {
            return true;
          } else if (above >= x.x0 && above <= x.x1) {
            return true;
          } else {
            return false;
          }
        }).map(x => ({x: width * x.i}));

        let oData = {
          data: dOld.map((x, i) => ({
            length: x.length, 
            x0: x.x0, 
            x1: x.x1,
            y: this.chart.height - ratio * x.length,
            x: width * i,
            width,
            height: ratio * x.length
          })),
          maxLength,
          max,
          min,
          median: median(old).toFixed(3),
          mean: avg,
          // percentiles: this.percentiles(old),
          deviation: dev,
          stdLines
        };

        data.push({
          young: yData,
          old: oData,
          element
        });
      }
      return data;
    },
    histogramFunction(v, x) {
      let histFunc =  histogram()
        .domain(x.domain())  
        .thresholds(x.ticks(100));

      return histFunc(v);
    },
    scale(min, max) {
      let  x = scaleLinear()
        .domain([min, max])     
        .range([0, this.chart.width]);
      // let y = scaleLinear()
      //   .domain([0, maxLength]) 
      //   .range([this.chart.height, 0]);
      return {x};
    },
    percentiles(data) {
      let percents = [.1, .2, .3, .4, .5, .6, .7, .8, .9];
      return percents.map(x => ({p: quantile(data, x), x}));
    },
    createExcelExport() {
      const elements = {
        "Sugar": "sugar",
        "pH": "pH",
        "EC": "EC",
        "K": "potassium",
        "Ca": "calcium",
        "Mg": "magnesium",
        "Na": "sodium",
        "NH4": "ammonium",
        "NO3": "NO3_nitrate",
        "N-NO3": "nitrogen_in_nitrate",
        "N": "nitrogen",
        "Cl": "chloride",
        "S": "sulfur",
        "P": "phosphorous",
        "Si": "silica",
        "Fe": "iron",
        "Mn": "manganese",
        "Zn": "zinc",
        "B": "boron",
        "Cu": "copper",
        "Mo": "molybdenum",
        "Al": "aluminium",
        "Co": "cobalt"
      };

      const headers = ["Std Deviation", "Min", "Max", "Mean", "Tmean", "Mean", "5%", "10%", "15%", "20%", "25%", "30%", "35%", "40%", "45%", "50%", "55%", "60%", "65%", "70%", "75%", "80%", "85%", "90%", "95%", "100%"];

      let data = [["Young", ...Object.keys(elements)]];
      let i = 1;
      for (let head of headers) {
        data[i] = [head];
        i++;
      }
      i++;
      data[i] = ["Old", ...Object.keys(elements)];
      i++;
      for (let head of headers) {
        data[i] = [head];
        i++;
      }

      for (let i = 0; i < this.elements.length; i++) { 
        const element = this.elements[i].key;    
        let { young, old } = this.cleanedValues;
        young = young[element];
        old = old[element];

        if (!young || !old) {
          continue;
        }

        let I = 1;

        I = this.compute(I, i, data, young);
        I += 2;
        this.compute(I, i, data, old);

        
        
      }

      saveJsonToExcel(data, "data.xlsx", {skipHeader: true});
    },
    compute(I, i, data, records) {
      let l = records.length;
      let trimmedY = records.sort((a, b) => a - b).slice(l * .1, l - (l * .1));
      data[I][i + 1] = deviation(records).toFixed(2);
      I++;
      data[I][i + 1] = Math.min(...records);
      I++;
      data[I][i + 1] = Math.max(...records);
      I++;
      data[I][i + 1] = mean(records).toFixed(3);
      I++;
      data[I][i + 1] = mean(trimmedY).toFixed(3);
      I++;
      data[I][i + 1] = median(records).toFixed(3);
      I++;

      let percents = [.05, .1, .15, .2, .25, .3, .35, .4, .45, .5, .55, .6, .65, .7, .75, .8, .85, .9, .95, 1];

      for (let p of percents) {
        data[I][i + 1] = quantile(records, p);
        I++;
      }
      return I;
    }
  },
  computed: {
    cleanedValues() {
      let youngValues = this.orders.filter(x => x.plantPart && x.plantPart.includes("young")).map(x => x.values);
      let oldValues = this.orders.filter(x => x.plantPart && !x.plantPart.includes("young")).map(x => x.values);
      let nTests = Math.min(youngValues.length, oldValues.length);

      let young = {};
      let old = {};
      for (let val1 of this.elements) {
        young[val1.key] = youngValues.flat(1).filter(x => x.name === val1.key).map(x => x.value).filter(x => !isNaN(x)).map(x => parseFloat(x));
        old[val1.key] = oldValues.flat(1).filter(x => x.name === val1.key).map(x => x.value).filter(x => !isNaN(x)).map(x => parseFloat(x));

        if (this.controls.trimBottom > 0 || this.controls.trimTop > 0 ) {
          let y = young[val1.key];
          let l = y.length;
          let percentB = (this.controls.trimBottom / 100) || 0;
          let percentT = this.controls.trimTop / 100;
          y = y.sort((a, b) => a - b).slice(l * percentB, l - (l * percentT));
          young[val1.key] = y;

          let o = old[val1.key];
          l = o.length;
          o = o.sort((a, b) => a - b).slice(l * percentB, l - (l * percentT));
          old[val1.key] = o;
        }
      }

      return {young, old, nTests};
    },
    ...mapGetters(["elements"])
  },
  beforeMount() {
    // for (let elm of this.elements) {
    //   this.individualCont.push({trimBottom: 0, trimTop: 0, element: elm.key});
    // }

    // window.onbeforeunload = () => {
    //   localStorage.lastSearch = JSON.stringify(this.orders);
    // };

    // if (localStorage.lastSearch) {
    //   try {
    //     this.orders = JSON.parse(localStorage.lastSearch);
    //   } catch(err) {
    //     // forget it
    //   }
    // }
  }
};
</script>

<style lang="scss" scoped>
.advanced {
  float: right;
}

.chart-cont {
  margin: 1em;
  display: inline-block;
  box-shadow: 0px 0px 4px gray; 
  padding: 1em;
  border-radius: 5px;
  background-color: white;
}

.stat {
  display: inline-block;
  margin-left: .5em;
  margin-right: .5em;
}
</style>